import { isBookingAuthError } from './bookingAuthRedirect';

export const ERROR_KIND = {
  NETWORK: 'network',
  AUTH: 'auth',
  ACTION_FAILED: 'action_failed',
  LOAD_FAILED: 'load_failed',
  VALIDATION: 'validation',
  BLOCKED: 'blocked',
  PAYMENT: 'payment',
  UPLOAD: 'upload',
  UNKNOWN: 'unknown',
};

const NETWORK_TOKENS = [
  'failed to fetch',
  'network request failed',
  'network error',
  'load failed',
  'net::err',
  'timeout',
  'timed out',
  'aborted',
  'aborterror',
  'econnreset',
  'enotfound',
];

const VALIDATION_TOKENS = [
  'validation error',
  'fieldundefined',
  'variable',
  'invalid input',
  'ungültig',
  'muss ausgefüllt',
  'ist erforderlich',
  'not allowed',
  'nicht erlaubt',
];

const BLOCKED_TOKENS = [
  'eigenen studio',
  'own studio',
  'bereits bewertet',
  'already submitted',
  'abgeschlossen. bitte schreibe',
  'chat wurde gelöscht',
  'diese anfrage ist abgeschlossen',
  'booking conflict',
  'buchungen an diesem tag',
  'buchungen in diesem zeitraum',
  'existiert bereits eine bewertung',
];

const PAYMENT_TOKENS = [
  'stripe',
  'payment',
  'zahlung',
  'card',
  'payout',
  'auszahlung',
];

const collectMessages = (error) => {
  const graphQLErrors = error?.graphQLErrors
    || error?.networkError?.result?.errors
    || [];

  return [
    error?.message,
    error?.networkError?.message,
    ...(Array.isArray(graphQLErrors) ? graphQLErrors.map((item) => item?.message) : []),
  ].filter(Boolean);
};

const normalizeText = (value) => String(value || '').toLowerCase();

const messagesMatchAny = (messages, tokens) => (
  messages.some((message) => {
    const normalized = normalizeText(message);
    return tokens.some((token) => normalized.includes(token));
  })
);

const hasNetworkFailure = (error) => {
  if (!error) return false;
  if (error.networkError && !error.graphQLErrors?.length) return true;

  const status = Number(
    error.statusCode
    || error.status
    || error.networkError?.statusCode
    || error.networkError?.status
  );

  if (status === 0) return true;

  return messagesMatchAny(collectMessages(error), NETWORK_TOKENS);
};

const isValidationError = (error) => {
  if (error?.userFacing === false) return true;

  const graphQLErrors = error?.graphQLErrors || [];
  if (graphQLErrors.some((item) => (
    item?.errorType === 'ValidationError'
    || item?.extensions?.validationError
  ))) {
    return true;
  }

  return messagesMatchAny(collectMessages(error), VALIDATION_TOKENS);
};

const isBlockedError = (error) => {
  if (error?.code === 'BLOCKED') return true;
  return messagesMatchAny(collectMessages(error), BLOCKED_TOKENS);
};

const isPaymentError = (error) => messagesMatchAny(collectMessages(error), PAYMENT_TOKENS);

const defaultCopy = {
  [ERROR_KIND.NETWORK]: {
    title: 'Verbindungsproblem',
    message: 'Keine stabile Verbindung. Bitte prüfe dein Internet und versuche es erneut.',
  },
  [ERROR_KIND.AUTH]: {
    title: 'Sitzung abgelaufen',
    message: 'Deine Anmeldung ist abgelaufen. Bitte melde dich erneut an.',
  },
  [ERROR_KIND.ACTION_FAILED]: {
    title: 'Aktion fehlgeschlagen',
    message: 'Deine Änderungen konnten nicht gespeichert werden. Bitte versuche es erneut.',
  },
  [ERROR_KIND.LOAD_FAILED]: {
    title: 'Laden fehlgeschlagen',
    message: 'Die Daten konnten nicht geladen werden. Bitte versuche es erneut.',
  },
  [ERROR_KIND.VALIDATION]: {
    title: 'Eingabe prüfen',
    message: 'Bitte überprüfe deine Eingaben und versuche es erneut.',
  },
  [ERROR_KIND.BLOCKED]: {
    title: 'Nicht möglich',
    message: 'Diese Aktion ist derzeit nicht möglich.',
  },
  [ERROR_KIND.PAYMENT]: {
    title: 'Zahlungsproblem',
    message: 'Die Zahlung konnte nicht abgeschlossen werden. Bitte versuche es erneut.',
  },
  [ERROR_KIND.UPLOAD]: {
    title: 'Upload fehlgeschlagen',
    message: 'Die Datei konnte nicht hochgeladen werden. Bitte versuche es erneut.',
  },
  [ERROR_KIND.UNKNOWN]: {
    title: 'Ein Fehler ist aufgetreten',
    message: 'Etwas ist schiefgelaufen. Bitte versuche es erneut.',
  },
};

/**
 * @param {unknown} error
 * @param {{ operation?: 'query' | 'mutation' | 'subscription', fallbackMessage?: string }} [options]
 */
export const classifyError = (error, options = {}) => {
  const messages = collectMessages(error);
  const firstMessage = messages[0] || options.fallbackMessage || '';
  const operation = options.operation || 'query';

  let kind = ERROR_KIND.UNKNOWN;

  if (isBookingAuthError(error)) {
    kind = ERROR_KIND.AUTH;
  } else if (hasNetworkFailure(error)) {
    kind = ERROR_KIND.NETWORK;
  } else if (isValidationError(error)) {
    kind = ERROR_KIND.VALIDATION;
  } else if (isBlockedError(error)) {
    kind = ERROR_KIND.BLOCKED;
  } else if (isPaymentError(error)) {
    kind = ERROR_KIND.PAYMENT;
  } else if (normalizeText(firstMessage).includes('upload') || normalizeText(firstMessage).includes('hochlad')) {
    kind = ERROR_KIND.UPLOAD;
  } else if (operation === 'mutation') {
    kind = ERROR_KIND.ACTION_FAILED;
  } else if (operation === 'query' || operation === 'subscription') {
    kind = ERROR_KIND.LOAD_FAILED;
  }

  const copy = defaultCopy[kind] || defaultCopy[ERROR_KIND.UNKNOWN];
  const userMessage = (error?.userFacing && firstMessage)
    || (kind === ERROR_KIND.VALIDATION || kind === ERROR_KIND.BLOCKED ? firstMessage : '')
    || copy.message;

  return {
    kind,
    title: copy.title,
    message: userMessage || copy.message,
    rawMessage: firstMessage,
    canRetry: kind === ERROR_KIND.NETWORK
      || kind === ERROR_KIND.ACTION_FAILED
      || kind === ERROR_KIND.LOAD_FAILED
      || kind === ERROR_KIND.UPLOAD,
    shouldShowGlobally: kind !== ERROR_KIND.VALIDATION && kind !== ERROR_KIND.BLOCKED,
  };
};

export default classifyError;
